import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  PALETTE_PRESETS,
  DEFAULT_PALETTE,
  DEPTH_LEVELS,
  FOUNDATION_PRESETS,
  getIntensityElements,
  estimateBgLuminance,
  computeVisibleRows,
} from '../../constants/palette';
import {
  colors,
  FAMILY_HUES,
  hexToRgb,
  hexToHsl,
  nearestFamily,
} from '../../constants/colors';
import {
  extractBackgroundColors,
  buildPaletteFromHue,
  hslToHex,
  padSpectrum,
} from '../../utils/colorExtract';
import { SOLID_ROWS } from '../../constants/backgrounds';

// ─── Constants ──────────────────────────────────────────────────────

const HARMONY_TYPES = [
  { id: 'monochromatic',        label: 'Mono',       short: 'Mono' },
  { id: 'analogous',            label: 'Analogous',  short: 'Analog' },
  { id: 'complementary',        label: 'Complement', short: 'Compl' },
  { id: 'split-complementary',  label: 'Split',      short: 'Split' },
  { id: 'triadic',              label: 'Triadic',    short: 'Triad' },
  { id: 'tetradic',             label: 'Tetradic',   short: 'Tetra' },
  { id: 'square',               label: 'Square',     short: 'Square' },
];

const ACHROMATIC_COLUMNS = [
  { id: 'monochromatic',        short: 'Neutral', hue: null },
  { id: 'analogous',            short: 'Gold',    hue: 45 },
  { id: 'complementary',        short: 'Blue',    hue: 199 },
  { id: 'split-complementary',  short: 'Green',   hue: 152 },
  { id: 'triadic',              short: 'Violet',  hue: 258 },
  { id: 'tetradic',             short: 'Rose',    hue: 0 },
  { id: 'square',               short: 'Orange',  hue: 25 },
];

const SAVED_PALETTES_KEY = 'mf_saved_palettes';

// Default palettes — always present, cannot be removed
const DEFAULT_SAVED_IDS = ['forest', 'paper'];

// ─── Clamp lightness to readable range on dark backgrounds ──────────

function clampLight(l) {
  return Math.max(50, Math.min(78, l));
}

// ─── Utility: get readable base label ────────────────────────────────

function getBaseLabel(source, spectrum, specIdx, pickedColors, browsingColor, savedPalettesArr) {
  if (source.startsWith('foundation-')) {
    const id = source.replace('foundation-', '');
    const preset = FOUNDATION_PRESETS.find(p => p.id === id);
    return preset ? preset.label : 'Custom';
  }
  if (source === 'bg' && spectrum && spectrum[specIdx]) {
    const c = spectrum[specIdx];
    const family = nearestFamily(c.h);
    // Capitalize first letter
    return family.charAt(0).toUpperCase() + family.slice(1);
  }
  if (source.startsWith('picked-')) {
    const idx = parseInt(source.split('-')[1], 10);
    if (pickedColors[idx]) {
      const family = nearestFamily(pickedColors[idx].h);
      return family.charAt(0).toUpperCase() + family.slice(1);
    }
  }
  if (source.startsWith('saved-base-') && savedPalettesArr) {
    const savedId = source.replace('saved-base-', '');
    const saved = savedPalettesArr.find(p => p.id === savedId);
    if (saved) return labelFromHex(saved.baseHex);
  }
  if (source === 'browsing' && browsingColor) {
    const family = nearestFamily(browsingColor.h);
    return family.charAt(0).toUpperCase() + family.slice(1);
  }
  return 'Custom';
}

// Derive label from hex color (for saved palette names when source is ambiguous)
function labelFromHex(hex) {
  if (!hex) return 'Custom';
  const hsl = hexToHsl(hex);
  if (!hsl) return 'Custom';
  // Very low saturation = achromatic
  if (hsl.s < 10) {
    if (hsl.l > 85) return 'White';
    if (hsl.l < 20) return 'Black';
    return 'Gray';
  }
  const family = nearestFamily(hsl.h);
  return family.charAt(0).toUpperCase() + family.slice(1);
}


// ─── Evocative palette naming ────────────────────────────────────────
// Instead of jargon like "Gold Split Deep", generate paint-swatch-style names

const COLOR_NAMES = {
  white:  ['Pearl', 'Ivory', 'Snow', 'Frost', 'Cloud', 'Chalk', 'Linen', 'Milk'],
  gray:   ['Slate', 'Silver', 'Ash', 'Smoke', 'Pewter', 'Stone', 'Steel', 'Fog'],
  black:  ['Onyx', 'Charcoal', 'Jet', 'Obsidian', 'Raven', 'Carbon', 'Eclipse', 'Noir'],
  gold:   ['Honey', 'Amber', 'Saffron', 'Topaz', 'Citrine', 'Marigold', 'Canary', 'Wheat'],
  green:  ['Moss', 'Fern', 'Sage', 'Ivy', 'Jade', 'Clover', 'Pine', 'Olive'],
  cyan:   ['Glacier', 'Lagoon', 'Teal', 'Arctic', 'Aqua', 'Mint', 'Breeze', 'Spray'],
  blue:   ['Ocean', 'Azure', 'Cobalt', 'Denim', 'Indigo', 'Sapphire', 'Harbor', 'Navy'],
  violet: ['Plum', 'Lilac', 'Amethyst', 'Orchid', 'Iris', 'Thistle', 'Wisteria', 'Mauve'],
  rose:   ['Coral', 'Blush', 'Peony', 'Petal', 'Rouge', 'Flamingo', 'Rosebud', 'Carnation'],
  red:    ['Ember', 'Crimson', 'Scarlet', 'Ruby', 'Garnet', 'Flame', 'Poppy', 'Vermilion'],
  orange: ['Copper', 'Tangerine', 'Paprika', 'Sunset', 'Terracotta', 'Clay', 'Rust', 'Apricot'],
};

const MOOD_WORDS = {
  monochromatic:       ['Still', 'Pure', 'Calm', 'Serene', 'Quiet', 'Dream'],
  analogous:           ['Bloom', 'Garden', 'Wave', 'Drift', 'Flow', 'Meadow'],
  complementary:       ['Spark', 'Duo', 'Flash', 'Contrast', 'Bold', 'Vivid'],
  'split-complementary': ['Prism', 'Aurora', 'Horizon', 'Dusk', 'Dawn', 'Twilight'],
  triadic:             ['Festival', 'Carousel', 'Trio', 'Jubilee', 'Fiesta', 'Mosaic'],
  tetradic:            ['Tapestry', 'Bazaar', 'Quilt', 'Spectrum', 'Palette', 'Collage'],
  square:              ['Compass', 'Solstice', 'Equinox', 'Orbit', 'Crown', 'Gem'],
};

function generatePaletteName(baseLabel, harmonyType, depthId, existingNames) {
  // Determine color word pool from baseLabel
  const key = baseLabel.toLowerCase();
  const colorPool = COLOR_NAMES[key] || COLOR_NAMES[nearestFamily(0)] || ['Custom'];
  const moodPool = MOOD_WORDS[harmonyType] || ['Mix'];

  const used = new Set(existingNames || []);

  // Try each color+mood combination until we find an unused one
  for (const color of colorPool) {
    for (const mood of moodPool) {
      const name = `${color} ${mood}`;
      if (!used.has(name.toUpperCase())) return name;
    }
  }

  // Fallback: add number
  for (let i = 2; i <= 99; i++) {
    const name = `${colorPool[0]} ${moodPool[0]} ${i}`;
    if (!used.has(name.toUpperCase())) return name;
  }

  return `${colorPool[0]} ${moodPool[0]}`;
}

// ─── Saved palette fingerprint (for dedup) ──────────────────────────

function savedFingerprint(baseHex, harmonyType, depthId) {
  return `${baseHex}|${harmonyType}|${depthId}`;
}

// ─── localStorage helpers ───────────────────────────────────────────

function loadSavedPalettes() {
  try {
    const raw = localStorage.getItem(SAVED_PALETTES_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function persistSavedPalettes(palettes) {
  try {
    localStorage.setItem(SAVED_PALETTES_KEY, JSON.stringify(palettes));
  } catch { /* quota exceeded — ignore */ }
}

// ─── 2×2 Color Swatch Grid ─────────────────────────────────────────

function PaletteSwatchGrid({ paletteColors, elements }) {
  const isFlat = paletteColors.accent === paletteColors.accentSoft;

  if (isFlat) {
    return (
      <div className="color-swatch-grid">
        {[0,1,2,3].map(i => (
          <span key={i} className="color-swatch-cell" style={{ backgroundColor: paletteColors.accent }} />
        ))}
      </div>
    );
  }

  const titleDef    = elements?.title    || { source: 'text', opacity: 1.0 };
  const bodyDef     = elements?.body     || { source: 'text', opacity: 0.75 };

  const sourceMap = {
    text:       paletteColors.textPrimary,
    accent:     paletteColors.accent,
    accentSoft: paletteColors.accentSoft,
  };

  const resolveWithOpacity = (def) => {
    const base = sourceMap[def.source] || paletteColors.textPrimary;
    const alpha = def.opacity ?? 1;
    if (alpha >= 0.95) return base;
    const rgb = hexToRgb(base);
    if (!rgb) return base;
    return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
  };

  const cells = [
    resolveWithOpacity(titleDef),
    resolveWithOpacity(bodyDef),
    paletteColors.accent,
    paletteColors.accentSoft,
  ];

  return (
    <div className="color-swatch-grid">
      {cells.map((color, i) => (
        <span key={i} className="color-swatch-cell" style={{ backgroundColor: color }} />
      ))}
    </div>
  );
}

// ─── Pin SVG icon (same as TemplatePicker) ──────────────────────────

function PinIcon({ className = "w-2.5 h-2.5" }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M4.146.146A.5.5 0 0 1 4.5 0h7a.5.5 0 0 1 .5.5c0 .68-.342 1.174-.646 1.479-.126.125-.25.224-.354.298v4.431l.078.048c.203.127.476.314.751.555C12.36 7.775 13 8.527 13 9.5a.5.5 0 0 1-.5.5h-4v4.5c0 .276-.224 1.5-.5 1.5s-.5-1.224-.5-1.5V10h-4a.5.5 0 0 1-.5-.5c0-.973.64-1.725 1.17-2.189A5.921 5.921 0 0 1 5 6.708V2.277a2.77 2.77 0 0 1-.354-.298C4.342 1.674 4 1.179 4 .5a.5.5 0 0 1 .146-.354z" />
    </svg>
  );
}

function CrossIcon({ className = "w-2 h-2" }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
    </svg>
  );
}

// ─── Stable background fingerprint ──────────────────────────────────

function bgFingerprint(slide) {
  if (!slide) return '';
  return JSON.stringify({
    bgSolid: slide.bgSolid || null,
    bgIndex: slide.bgIndex ?? null,
    backgroundUrl: slide.backgroundUrl || null,
    gradientEnabled: slide.gradient?.enabled || false,
    gradientBase: slide.gradient?.base || null,
    gradientAccent1: slide.gradient?.accent1 || null,
    gradientAccent2: slide.gradient?.accent2 || null,
    generativeEnabled: slide.generative?.enabled || false,
    generativeType: slide.generative?.type || null,
    generativePaletteId: slide.generative?.paletteId || null,
    generativeHueShift: slide.generative?.hueShift ?? 0,
    generativeSaturation: slide.generative?.saturation ?? 100,
    generativeBrightness: slide.generative?.brightness ?? 100,
    generativeBase: slide.generative?.base || null,
    generativeAccent: slide.generative?.accent || null,
    p3dEnabled: slide.primitives3d?.enabled || false,
    p3dBg: slide.primitives3d?.bgColor || null,
    p3dFace: slide.primitives3d?.faceColor || null,
  });
}

// ─── PalettePicker ──────────────────────────────────────────────────

export function PalettePicker({
  value,
  customPalette,
  onChange,
  slides = [],
  activeSlide = null,
  scope = 'slide',
  onScopeChange,
  onBulkChange,
  template = 'forest-classic',
}) {
  const currentId = value || DEFAULT_PALETTE;

  // ── State ──────────────────────────────────────────────────────────
  const [harmonyType, setHarmonyType] = useState('monochromatic');
  const [depthLevel, setDepthLevel] = useState('direct');
  const [selectedSpectrumIdx, setSelectedSpectrumIdx] = useState(0);

  // Base source: 'foundation-<id>' for canonical hues, 'bg' for extracted, 'picked-N', 'browsing'
  const [baseSource, setBaseSource] = useState('bg');
  const [pickedColors, setPickedColors] = useState([]);
  const [browsingColor, setBrowsingColor] = useState(null);
  const [showFamilies, setShowFamilies] = useState(false);

  // Track if user has explicitly chosen a palette (prevents auto-apply override)
  const [userHasChosen, setUserHasChosen] = useState(false);

  // Track the preset the user had before applying harmony (to revert)
  const preApplyPresetRef = useRef(currentId !== 'custom' ? currentId : DEFAULT_PALETTE);

  // Spectrum (ALWAYS extracted — independent of toggle)
  const [sampling, setSampling] = useState(false);
  const [spectrum, setSpectrum] = useState(null);

  // Saved palettes (localStorage-backed)
  const [savedPalettes, setSavedPalettes] = useState(() => loadSavedPalettes());

  // Refs for latest values in async callbacks
  const harmonyTypeRef = useRef(harmonyType);
  const depthLevelRef = useRef(depthLevel);
  const selectedSpectrumIdxRef = useRef(selectedSpectrumIdx);
  const baseSourceRef = useRef(baseSource);
  harmonyTypeRef.current = harmonyType;
  depthLevelRef.current = depthLevel;
  selectedSpectrumIdxRef.current = selectedSpectrumIdx;
  baseSourceRef.current = baseSource;

  const bgKey = useMemo(() => bgFingerprint(activeSlide), [activeSlide]);

  // ── Context-aware visible rows ──────────────────────────────────────
  const visibleRows = useMemo(() => {
    return computeVisibleRows(activeSlide, template);
  }, [activeSlide, template]);

  // ── Persist saved palettes ──────────────────────────────────────────
  useEffect(() => {
    persistSavedPalettes(savedPalettes);
  }, [savedPalettes]);

  // ── Default palette auto-application ────────────────────────────────
  // Until user explicitly clicks a base color or palette, show the correct default
  useEffect(() => {
    if (userHasChosen) return;
    if (!activeSlide) return;

    const bg = estimateBgLuminance(activeSlide);
    if (bg.computable && bg.isLight) {
      // Light background → Dark Ink (paper)
      if (currentId !== 'paper') {
        const patch = { palette: 'paper', paletteCustom: null };
        if (scope === 'all' && onBulkChange) {
          onBulkChange(patch, 'Auto: Dark Ink for light background');
        } else {
          onChange(patch);
        }
      }
    } else {
      // Dark background → Magic Forest (forest)
      if (currentId !== 'forest' && currentId !== 'custom') {
        // Only auto-switch if still on default — don't override custom
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgKey]);

  // ── Apply palette to slide ─────────────────────────────────────────

  const applyToSlide = useCallback((generated, hint) => {
    setUserHasChosen(true);
    const patch = { palette: 'custom', paletteCustom: generated };
    if (scope === 'all' && onBulkChange) {
      onBulkChange(patch, hint);
    } else {
      onChange(patch);
    }
  }, [scope, onChange, onBulkChange]);

  // ── Generate palette from base + harmony + depth ───────────────────

  const generatePalette = useCallback((base, type, depth) => {
    if (!base) return null;
    const depthDef = DEPTH_LEVELS.find(d => d.id === depth) || DEPTH_LEVELS[0];
    const elements = getIntensityElements(depthDef.elementsId);
    const baseHue = base.h;
    const baseSat = Math.max(base.s, 50);
    const baseLight = clampLight(base.l);
    const realSat = base.realSat ?? null;
    return buildPaletteFromHue(baseHue, type, elements, baseSat, baseLight, depthDef.anchorToBase, realSat, base.hex);
  }, []);

  // ── Get effective base colour from source state ────────────────────

  const getEffectiveBase = useCallback((source, spectrumArr, specIdx) => {
    if (source === 'bg') {
      if (spectrumArr && spectrumArr.length > 0) {
        return spectrumArr[Math.min(specIdx, spectrumArr.length - 1)];
      }
      return null;
    }
    if (source.startsWith('foundation-')) {
      const id = source.replace('foundation-', '');
      const preset = FOUNDATION_PRESETS.find(p => p.id === id);
      if (!preset) return null;
      return {
        hex: preset.hex, h: preset.hue, s: preset.sat, l: preset.light,
        realSat: preset.realSat ?? preset.sat,
      };
    }
    if (source.startsWith('picked-')) {
      const idx = parseInt(source.split('-')[1], 10);
      if (pickedColors[idx]) return pickedColors[idx];
      return null;
    }
    if (source.startsWith('saved-base-')) {
      const savedId = source.replace('saved-base-', '');
      const saved = savedPalettes.find(p => p.id === savedId);
      if (!saved) return null;
      const hsl = hexToHsl(saved.baseHex);
      if (!hsl) return null;
      return { hex: saved.baseHex, h: hsl.h, s: hsl.s, l: hsl.l };
    }
    if (source === 'browsing' && browsingColor) {
      return browsingColor;
    }
    const hue = FAMILY_HUES[source];
    if (hue === undefined) return null;
    return { hex: colors[source][400], h: hue, s: 70, l: 50 };
  }, [pickedColors, browsingColor, savedPalettes]);

  // ── Regenerate and apply ───────────────────────────────────────────

  const regenerateAndApply = useCallback((src, specArr, specIdx, type, depth) => {
    const base = getEffectiveBase(src, specArr, specIdx);
    if (!base) return;
    const palette = generatePalette(base, type, depth);
    if (!palette) return;
    const depthDef = DEPTH_LEVELS.find(d => d.id === depth) || DEPTH_LEVELS[0];
    const typeLabel = HARMONY_TYPES.find(t => t.id === type)?.label || type;
    applyToSlide(palette, `${depthDef.label} · ${typeLabel}`);
  }, [getEffectiveBase, generatePalette, applyToSlide]);

  // ── Extract spectrum (ALWAYS runs on bg change) ────────────────────

  const extractSpectrum = useCallback(async () => {
    if (!activeSlide) return;
    setSampling(true);
    try {
      const extracted = await extractBackgroundColors(activeSlide);
      if (extracted.length === 0) { setSampling(false); return; }

      const padded = padSpectrum(extracted, 8);

      const spectrumColors = padded.slice(0, 8).map(c => ({
        hex: c.hex, h: c.h, s: c.s, l: c.l,
      }));
      setSpectrum(spectrumColors);
    } catch (err) {
      console.warn('[PalettePicker] Extraction failed:', err);
    }
    setSampling(false);
  }, [activeSlide]);

  useEffect(() => {
    extractSpectrum();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bgKey]);

  // ── Preset click (for backward compat — saved palettes) ────────────

  // Mapping: preset ID → foundation base + harmony + depth for grid activation
  const PRESET_BASE_MAP = useMemo(() => ({
    'forest': { foundationId: 'white', harmonyType: 'monochromatic', depthId: 'harmony' },
    'paper':  { foundationId: 'black', harmonyType: 'complementary', depthId: 'harmony' },
  }), []);

  const handlePresetClick = useCallback((presetId) => {
    setUserHasChosen(true);
    preApplyPresetRef.current = presetId;
    const name = PALETTE_PRESETS.find(p => p.id === presetId)?.name || presetId;
    const patch = { palette: presetId, paletteCustom: null };

    // Also activate the matching base color + harmony + depth so the grid reflects it
    const baseMap = PRESET_BASE_MAP[presetId];
    if (baseMap) {
      setBaseSource(`foundation-${baseMap.foundationId}`);
      setHarmonyType(baseMap.harmonyType);
      setDepthLevel(baseMap.depthId);
    }

    if (scope === 'all' && onBulkChange) {
      onBulkChange(patch, `Palette changed to ${name} on all slides`);
    } else {
      onChange(patch);
    }
  }, [scope, onChange, onBulkChange, PRESET_BASE_MAP]);

  // ── Foundation preset click ─────────────────────────────────────────

  const handleFoundationClick = useCallback((preset) => {
    setUserHasChosen(true);
    setBaseSource(`foundation-${preset.id}`);

    const base = { hex: preset.hex, h: preset.hue, s: preset.sat, l: preset.light };
    const depthDef = DEPTH_LEVELS.find(d => d.id === depthLevel) || DEPTH_LEVELS[0];
    const elements = getIntensityElements(depthDef.elementsId);
    const baseSat = Math.max(base.s, 50);
    const baseLight = clampLight(base.l);
    const realSat = preset.realSat ?? null;

    if (preset.presetId && preset.presetColumn === harmonyType && depthLevel === 'harmony') {
      handlePresetClick(preset.presetId);
      return;
    }

    const palette = buildPaletteFromHue(base.h, harmonyType, elements, baseSat, baseLight, depthDef.anchorToBase, realSat, preset.hex);
    if (palette) {
      const typeLabel = HARMONY_TYPES.find(t => t.id === harmonyType)?.label || harmonyType;
      applyToSlide(palette, `${depthDef.label} · ${typeLabel}`);
    }
  }, [depthLevel, harmonyType, applyToSlide, handlePresetClick]);

  // ── Spectrum dot click ──────────────────────────────────────────────

  const handleSpectrumSelect = useCallback((idx) => {
    setUserHasChosen(true);
    setSelectedSpectrumIdx(idx);
    setBaseSource('bg');
    if (spectrum) {
      regenerateAndApply('bg', spectrum, idx, harmonyType, depthLevel);
    }
  }, [spectrum, harmonyType, depthLevel, regenerateAndApply]);

  // ── Eyedropper ──────────────────────────────────────────────────────

  const eyedropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

  const handleEyedropperPick = useCallback(async () => {
    if (!eyedropperSupported) return;
    try {
      const dropper = new window.EyeDropper();
      const result = await dropper.open();
      const hex = result.sRGBHex;
      const hsl = hexToHsl(hex);
      const picked = { hex, h: hsl.h, s: hsl.s, l: hsl.l };
      const newIdx = pickedColors.length;
      setPickedColors(prev => [...prev, picked]);
      const newSource = `picked-${newIdx}`;
      setBaseSource(newSource);
      setUserHasChosen(true);
      const depthDef = DEPTH_LEVELS.find(d => d.id === depthLevel) || DEPTH_LEVELS[0];
      const elements = getIntensityElements(depthDef.elementsId);
      const baseSat = Math.max(picked.s, 50);
      const baseLight = clampLight(picked.l);
      const palette = buildPaletteFromHue(picked.h, harmonyType, elements, baseSat, baseLight, depthDef.anchorToBase, picked.s, hex);
      if (palette) {
        const typeLabel = HARMONY_TYPES.find(t => t.id === harmonyType)?.label || harmonyType;
        applyToSlide(palette, `${depthDef.label} · ${typeLabel}`);
      }
    } catch {
      // User cancelled
    }
  }, [eyedropperSupported, pickedColors.length, harmonyType, depthLevel, applyToSlide]);

  const handlePickedSelect = useCallback((idx) => {
    setUserHasChosen(true);
    const source = `picked-${idx}`;
    setBaseSource(source);
    regenerateAndApply(source, spectrum, selectedSpectrumIdx, harmonyType, depthLevel);
  }, [spectrum, selectedSpectrumIdx, harmonyType, depthLevel, regenerateAndApply]);

  const handlePickedRemove = useCallback((idx) => {
    setPickedColors(prev => prev.filter((_, i) => i !== idx));
    if (baseSource === `picked-${idx}`) {
      setBaseSource('bg');
      regenerateAndApply('bg', spectrum, selectedSpectrumIdx, harmonyType, depthLevel);
    } else if (baseSource.startsWith('picked-')) {
      const activeIdx = parseInt(baseSource.split('-')[1], 10);
      if (idx < activeIdx) {
        setBaseSource(`picked-${activeIdx - 1}`);
      }
    }
  }, [baseSource, spectrum, selectedSpectrumIdx, harmonyType, depthLevel, regenerateAndApply]);

  // ── "More colours" grid browse + pin ────────────────────────────────

  const handlePinColor = useCallback((hex) => {
    if (pickedColors.some(c => c.hex === hex)) return;
    const hsl = hexToHsl(hex);
    const picked = { hex, h: hsl.h, s: hsl.s, l: hsl.l };
    setPickedColors(prev => [...prev, picked]);
  }, [pickedColors]);

  const handleSwatchBrowse = useCallback((hex) => {
    setUserHasChosen(true);
    const hsl = hexToHsl(hex);
    const color = { hex, h: hsl.h, s: hsl.s, l: hsl.l };
    setBrowsingColor(color);
    setBaseSource('browsing');
    const depthDef = DEPTH_LEVELS.find(d => d.id === depthLevel) || DEPTH_LEVELS[0];
    const elements = getIntensityElements(depthDef.elementsId);
    const baseSat = Math.max(color.s, 50);
    const baseLight = clampLight(color.l);
    const palette = buildPaletteFromHue(color.h, harmonyType, elements, baseSat, baseLight, depthDef.anchorToBase, color.s, hex);
    if (palette) {
      const typeLabel = HARMONY_TYPES.find(t => t.id === harmonyType)?.label || harmonyType;
      applyToSlide(palette, `${depthDef.label} · ${typeLabel}`);
    }
  }, [harmonyType, depthLevel, applyToSlide]);

  // ── Saved base click (from Base Colours section) ────────────────────

  const resolveBaseSource = useCallback((saved) => {
    // If the saved palette's base matches a foundation preset, use that source
    // so the grid regenerates with the exact same parameters (incl. realSat)
    const matchingPreset = FOUNDATION_PRESETS.find(p => p.hex === saved.baseHex);
    if (matchingPreset) return `foundation-${matchingPreset.id}`;
    // If it stored a baseSource, try to restore it (bg, picked, browsing)
    if (saved.baseSourceType === 'bg') return 'bg';
    // Otherwise use saved-base
    return `saved-base-${saved.id}`;
  }, []);

  const handleSavedBaseClick = useCallback((saved) => {
    setUserHasChosen(true);
    const source = resolveBaseSource(saved);
    setBaseSource(source);
    setHarmonyType(saved.harmonyType);
    setDepthLevel(saved.depthId);
    regenerateAndApply(source, spectrum, selectedSpectrumIdx, saved.harmonyType, saved.depthId);
  }, [spectrum, selectedSpectrumIdx, regenerateAndApply, resolveBaseSource]);

  // ── Save / unsave palette ──────────────────────────────────────────

  const handleSavePalette = useCallback((baseHex, typeId, depthId, palette, elements) => {
    const fp = savedFingerprint(baseHex, typeId, depthId);
    const existing = savedPalettes.find(p => p.fingerprint === fp);

    if (existing) {
      // Remove
      setSavedPalettes(prev => prev.filter(p => p.fingerprint !== fp));
    } else {
      // Add
      let baseLabel = getBaseLabel(baseSource, spectrum, selectedSpectrumIdx, pickedColors, browsingColor, savedPalettes);
      // Fallback: derive name from the actual base hex if source lookup returned generic "Custom"
      if (baseLabel === 'Custom' && baseHex) {
        baseLabel = labelFromHex(baseHex);
      }
      const existingNames = savedPalettes.map(p => p.name.toUpperCase());
      const name = generatePaletteName(baseLabel, typeId, depthId, existingNames);
      // Store baseSourceType so we can reconstruct the exact base source later
      const baseSourceType = baseSource.startsWith('foundation-') ? 'foundation'
        : baseSource === 'bg' ? 'bg'
        : baseSource.startsWith('picked-') ? 'picked'
        : 'other';
      const newEntry = {
        id: `saved-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        fingerprint: fp,
        name,
        baseHex,
        baseSourceType,
        harmonyType: typeId,
        depthId,
        palette: { ...palette },
        elements: elements ? { ...elements } : {},
        createdAt: Date.now(),
      };
      setSavedPalettes(prev => [...prev, newEntry]);
    }
  }, [savedPalettes, baseSource, spectrum, selectedSpectrumIdx, pickedColors, browsingColor]);

  const handleRemoveSavedPalette = useCallback((paletteId) => {
    setSavedPalettes(prev => prev.filter(p => p.id !== paletteId));
  }, []);

  const handleApplySavedPalette = useCallback((saved) => {
    setUserHasChosen(true);
    const source = resolveBaseSource(saved);
    setBaseSource(source);
    setHarmonyType(saved.harmonyType);
    setDepthLevel(saved.depthId);
    applyToSlide(saved.palette, saved.name);
  }, [applyToSlide, resolveBaseSource]);

  // ── Determine which foundation preset is active ─────────────────────
  const activeFoundationId = baseSource.startsWith('foundation-')
    ? baseSource.replace('foundation-', '')
    : null;

  // ── Check if a cell is saved (user-saved OR default preset) ─────────

  const isCellSaved = useCallback((baseHex, typeId, depthId) => {
    // Check user-saved palettes
    const fp = savedFingerprint(baseHex, typeId, depthId);
    if (savedPalettes.some(p => p.fingerprint === fp)) return true;
    return false;
  }, [savedPalettes]);

  // Check if a grid cell corresponds to a default preset for the current foundation
  const cellMatchesDefaultPreset = useCallback((typeId, depthId) => {
    if (!activeFoundationId) return null;
    const preset = FOUNDATION_PRESETS.find(p => p.id === activeFoundationId);
    if (!preset || !preset.presetId) return null;
    if (preset.presetColumn === typeId && depthId === 'harmony') {
      return preset.presetId; // 'forest' or 'paper'
    }
    return null;
  }, [activeFoundationId]);

  // ── Pre-computed harmony grid ──────────────────────────────────────

  const harmonyGrid = useMemo(() => {
    const base = getEffectiveBase(baseSource, spectrum, selectedSpectrumIdx);
    if (!base) return null;
    const baseSat = Math.max(base.s, 50);
    const baseLight = clampLight(base.l);
    const realSat = base.realSat ?? null;
    return visibleRows.map(depth => ({
      depth,
      cells: HARMONY_TYPES.map(ht => {
        const elements = getIntensityElements(depth.elementsId);
        const palette = buildPaletteFromHue(base.h, ht.id, elements, baseSat, baseLight, depth.anchorToBase, realSat, base.hex);
        return { type: ht, depth, palette, elements, baseHex: base.hex };
      }),
    }));
  }, [baseSource, spectrum, selectedSpectrumIdx, visibleRows, getEffectiveBase]);

  // ── Unique base colors from saved palettes (for Base Colours section) ──
  // Only show bases that DON'T already exist in foundation presets (Row A)
  const savedBases = useMemo(() => {
    // Collect ALL hex values already visible in Row A (foundations) and Row B (spectrum + picked)
    const existingHexes = new Set(FOUNDATION_PRESETS.map(p => p.hex));
    if (spectrum) spectrum.forEach(c => existingHexes.add(c.hex));
    pickedColors.forEach(c => existingHexes.add(c.hex));

    const seen = new Set();
    return savedPalettes
      .filter(p => {
        // Skip if this base color already exists in any visible row
        if (existingHexes.has(p.baseHex)) return false;
        if (seen.has(p.baseHex)) return false;
        seen.add(p.baseHex);
        return true;
      })
      .map(p => ({
        id: p.id,
        hex: p.baseHex,
        name: p.name,
      }));
  }, [savedPalettes, spectrum, pickedColors]);

  // ── Detect achromatic base for column header styling ────────────────
  const isAchromaticBase = useMemo(() => {
    const base = getEffectiveBase(baseSource, spectrum, selectedSpectrumIdx);
    if (!base) return false;
    const rs = base.realSat !== undefined ? base.realSat : base.s;
    return rs < 15;
  }, [baseSource, spectrum, selectedSpectrumIdx, getEffectiveBase]);

  // ── Grid cell click ─────────────────────────────────────────────────

  const handleGridCellClick = useCallback((typeId, depthId, palette) => {
    setUserHasChosen(true);
    setHarmonyType(typeId);
    setDepthLevel(depthId);

    if (depthId === 'harmony' && activeFoundationId) {
      const preset = FOUNDATION_PRESETS.find(p => p.id === activeFoundationId);
      if (preset?.presetId && preset.presetColumn === typeId) {
        handlePresetClick(preset.presetId);
        return;
      }
    }

    const depthDef = DEPTH_LEVELS.find(d => d.id === depthId) || DEPTH_LEVELS[0];
    const typeLabel = HARMONY_TYPES.find(t => t.id === typeId)?.label || typeId;
    applyToSlide(palette, `${depthDef.label} · ${typeLabel}`);
  }, [applyToSlide, activeFoundationId, handlePresetClick]);

  // ── Derive selected cell's accent for highlighting ──────────────────
  const selectedAccentHex = (() => {
    if (!harmonyGrid) return null;
    for (const row of harmonyGrid) {
      for (const cell of row.cells) {
        if (cell.type.id === harmonyType && cell.depth.id === depthLevel && cell.palette) {
          return cell.palette.accent;
        }
      }
    }
    return null;
  })();
  const selectedAccentRgb = selectedAccentHex ? hexToRgb(selectedAccentHex) : null;
  const selectedAccentIsDark = selectedAccentRgb
    && (selectedAccentRgb.r * 0.299 + selectedAccentRgb.g * 0.587 + selectedAccentRgb.b * 0.114) < 60;
  // For dark accents, use a light substitute so labels/LEDs stay visible
  const effectiveAccentHex = selectedAccentIsDark ? '#e0e0e0' : selectedAccentHex;
  const effectiveAccentGlow = selectedAccentIsDark
    ? 'rgba(255,255,255,0.4)'
    : (selectedAccentRgb
      ? `rgba(${selectedAccentRgb.r},${selectedAccentRgb.g},${selectedAccentRgb.b},0.4)`
      : null);

  return (
    <div className="palette-block">

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ① BASE COLOURS                                            */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="palette-section">
        <div className="harmony-section-label">Base Colour</div>

        {/* Row A: Canonical hues from TEXT_ACCENT_COLORS */}
        <div className="palette-bg-row" style={{ marginBottom: '10px' }}>
          {FOUNDATION_PRESETS.map((preset) => {
            const isActive = activeFoundationId === preset.id;
            const accentRgb = hexToRgb(preset.hex);
            const activeGlow = accentRgb && preset.id !== 'black'
              ? `rgba(${accentRgb.r},${accentRgb.g},${accentRgb.b},0.35)`
              : 'rgba(255,255,255,0.15)';
            return (
              <button
                key={preset.id}
                type="button"
                title={preset.label}
                onClick={() => handleFoundationClick(preset)}
                className={`harmony-color-well${isActive ? ' harmony-color-well-active' : ''}`}
                style={isActive ? {
                  boxShadow: `0 0 0 1.5px ${activeGlow}, inset 0 2px 4px rgba(0,0,0,0.7)`,
                } : undefined}
              >
                <span className="harmony-color-dot" style={{ backgroundColor: preset.hex }} />
              </button>
            );
          })}
        </div>

        {/* Row B: Extracted background colours + tools */}
        <div className="palette-bg-row">
          {spectrum && spectrum.length > 0 ? (
            spectrum.map((c, i) => {
              const active = baseSource === 'bg' && selectedSpectrumIdx === i;
              return (
                <button
                  key={`bg-${i}`}
                  type="button"
                  title={c.hex}
                  onClick={() => handleSpectrumSelect(i)}
                  className={`harmony-color-well${active ? ' harmony-color-well-active' : ''}`}
                >
                  <span className="harmony-color-dot" style={{ backgroundColor: c.hex }} />
                </button>
              );
            })
          ) : (
            <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.15)', fontStyle: 'italic', lineHeight: '28px' }}>
              {sampling ? 'Sampling background…' : 'No background colours'}
            </div>
          )}

          {/* Eyedropper-picked colors */}
          {pickedColors.map((c, i) => {
            const active = baseSource === `picked-${i}`;
            return (
              <div key={`pick-${i}`} className="harmony-picked-wrap">
                <button
                  type="button"
                  title={`Picked: ${c.hex}`}
                  onClick={() => handlePickedSelect(i)}
                  className={`harmony-color-well${active ? ' harmony-color-well-active' : ''}`}
                >
                  <span className="harmony-color-dot" style={{ backgroundColor: c.hex }} />
                </button>
                <button
                  type="button"
                  title="Remove"
                  className="harmony-picked-remove"
                  onClick={(e) => { e.stopPropagation(); handlePickedRemove(i); }}
                >
                  ×
                </button>
              </div>
            );
          })}

          {/* ⊕ More colours + 🔍 Eyedropper */}
          <button
            type="button"
            onClick={() => setShowFamilies(prev => !prev)}
            className="palette-tool-btn"
            title="More colours"
          >
            <span style={{ fontSize: '14px', lineHeight: 1 }}>⊕</span>
          </button>

          {eyedropperSupported && (
            <button
              type="button"
              title="Pick colour from screen"
              onClick={handleEyedropperPick}
              className="palette-tool-btn"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m2 22 .414-.414" />
                <path d="m12 9-8.414 8.414A2 2 0 0 0 3 18.828v1.344a2 2 0 0 1-.586 1.414A2 2 0 0 1 3.828 21h1.344a2 2 0 0 0 1.414-.586L15 12" />
                <path d="m18 9 .4.4a1 1 0 1 1-3 3l-3.8-3.8a1 1 0 1 1 3-3l.4.4 3.4-3.4a1 1 0 1 1 3 3z" />
              </svg>
            </button>
          )}
        </div>

        {/* Row C: Base colours from saved palettes */}
        {savedBases.length > 0 && (
          <div className="palette-bg-row" style={{ marginTop: '6px' }}>
            {savedBases.map((sb) => {
              // Active if baseSource points to this saved base OR
              // the effective base hex matches this saved base's hex
              const directMatch = baseSource === `saved-base-${sb.id}`;
              const effectiveBase = getEffectiveBase(baseSource, spectrum, selectedSpectrumIdx);
              const hexMatch = effectiveBase && effectiveBase.hex === sb.hex;
              const active = directMatch || hexMatch;
              const accentRgb = hexToRgb(sb.hex);
              const activeGlow = accentRgb
                ? `rgba(${accentRgb.r},${accentRgb.g},${accentRgb.b},0.35)`
                : 'rgba(255,255,255,0.15)';
              // Find the saved palette to pass to handler
              const savedPalette = savedPalettes.find(p => p.id === sb.id);
              return (
                <button
                  key={`sb-${sb.id}`}
                  type="button"
                  title={`Saved: ${sb.name}`}
                  onClick={() => savedPalette && handleSavedBaseClick(savedPalette)}
                  className={`harmony-color-well${active ? ' harmony-color-well-active' : ''}`}
                  style={active ? {
                    boxShadow: `0 0 0 1.5px ${activeGlow}, inset 0 2px 4px rgba(0,0,0,0.7)`,
                  } : undefined}
                >
                  <span className="harmony-color-dot" style={{ backgroundColor: sb.hex }} />
                </button>
              );
            })}
          </div>
        )}

        {/* "More colours" grid (expandable) */}
        {showFamilies && (
          <div className="palette-families-grid">
            {SOLID_ROWS.map((row, ri) => (
              <div key={ri} className="flex flex-wrap gap-px">
                {row.map((c) => {
                  const isPinned = pickedColors.some(p => p.hex === c.hex);
                  const isBrowsing = baseSource === 'browsing' && browsingColor?.hex === c.hex;
                  return (
                    <div key={c.id} className="harmony-swatch-wrap">
                      <button
                        type="button"
                        title={c.name}
                        onClick={() => handleSwatchBrowse(c.hex)}
                        className={`harmony-swatch-cell${isBrowsing ? ' harmony-swatch-cell-active' : ''}`}
                      >
                        <span className="harmony-swatch-dot" style={{ backgroundColor: c.hex }} />
                      </button>
                      {!isPinned ? (
                        <button
                          type="button"
                          title={`Pin ${c.name}`}
                          className="harmony-swatch-pin"
                          onClick={(e) => { e.stopPropagation(); handlePinColor(c.hex); }}
                        >
                          +
                        </button>
                      ) : (
                        <span className="harmony-swatch-pinned" title="Pinned">✓</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ② HARMONIES — Context-aware grid with save pins            */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="palette-section">
        <div className="harmony-section-label">
          Harmonies
          {sampling && (
            <span className="ml-2 h-2 w-2 border border-white/25 border-t-white/60 rounded-full animate-spin inline-block" />
          )}
        </div>

        {harmonyGrid ? (
          <div className="palette-harmony-grid" style={{
            gridTemplateColumns: `36px repeat(7, 1fr)`,
            gridTemplateRows: `auto repeat(${visibleRows.length}, auto)`,
          }}>
            {/* Column headers */}
            <div /> {/* empty top-left corner */}
            {HARMONY_TYPES.map(ht => {
              const isColActive = harmonyType === ht.id;
              const achrCol = isAchromaticBase
                ? ACHROMATIC_COLUMNS.find(c => c.id === ht.id)
                : null;
              const headerLabel = achrCol ? achrCol.short : ht.short;

              let headerStyle;
              if (achrCol?.hue != null) {
                if (isColActive) {
                  const bright = hslToHex(achrCol.hue, 55, 65);
                  headerStyle = {
                    color: bright,
                    textShadow: `0 0 8px ${bright}88`,
                  };
                } else {
                  headerStyle = {
                    color: hslToHex(achrCol.hue, 30, 38),
                  };
                }
              } else if (isColActive && effectiveAccentHex) {
                headerStyle = {
                  color: effectiveAccentHex,
                  textShadow: `0 0 8px ${effectiveAccentGlow}`,
                };
              }

              return (
                <div
                  key={ht.id}
                  className={`harmony-grid-header${isColActive ? ' harmony-grid-header-active' : ''}`}
                  style={headerStyle}
                >
                  {headerLabel}
                </div>
              );
            })}

            {/* Data rows */}
            {harmonyGrid.map(row => (
              <React.Fragment key={row.depth.id}>
                {(() => {
                  const isRowActive = depthLevel === row.depth.id;
                  return (
                    <div
                      className={`palette-row-label${isRowActive ? ' palette-row-label-active' : ''}`}
                      style={isRowActive && effectiveAccentHex ? {
                        color: effectiveAccentHex,
                        textShadow: `0 0 8px ${effectiveAccentGlow}`,
                      } : undefined}
                      title={row.depth.description}
                    >
                      <span className="palette-row-label-name">{row.depth.short}</span>
                    </div>
                  );
                })()}
                {row.cells.map(cell => {
                  const isActive = harmonyType === cell.type.id && depthLevel === cell.depth.id;
                  const cellAccent = cell.palette?.accent;
                  const cellAccentRgb = cellAccent ? hexToRgb(cellAccent) : null;
                  const cellAccentGlow = cellAccentRgb
                    ? `rgba(${cellAccentRgb.r},${cellAccentRgb.g},${cellAccentRgb.b},0.5)`
                    : null;

                  // Check if this cell is saved (user-saved or default preset)
                  const cellBaseHex = cell.baseHex;
                  const cellIsSaved = cellBaseHex && isCellSaved(cellBaseHex, cell.type.id, cell.depth.id);
                  const cellDefaultPreset = cellMatchesDefaultPreset(cell.type.id, cell.depth.id);
                  const cellIsPinned = cellIsSaved || !!cellDefaultPreset;

                  return (
                    <button
                      key={`${cell.type.id}-${cell.depth.id}`}
                      type="button"
                      title={`${cell.type.label} · ${cell.depth.label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGridCellClick(cell.type.id, cell.depth.id, cell.palette);
                      }}
                      className={`harmony-grid-cell group${isActive ? ' harmony-grid-cell-active' : ''}`}
                    >
                      {/* LED dot — top right (inverted for dark accents) */}
                      {(() => {
                        const isLedDark = isActive && cellAccentRgb
                          && (cellAccentRgb.r * 0.299 + cellAccentRgb.g * 0.587 + cellAccentRgb.b * 0.114) < 60;
                        const ledColor = isLedDark ? '#e0e0e0' : cellAccent;
                        const ledGlow = isLedDark
                          ? 'rgba(255,255,255,0.4)'
                          : cellAccentGlow;
                        return (
                          <span
                            className={`harmony-grid-led${isActive ? ' harmony-grid-led-active' : ''}`}
                            style={isActive && cellAccent ? {
                              background: ledColor,
                              boxShadow: `inset 0 1px 1px rgba(255,255,255,0.3), 0 0 6px ${ledGlow}`,
                            } : undefined}
                          />
                        );
                      })()}

                      {/* Pin/save badge — top left */}
                      {cellIsPinned ? (
                        <span
                          className={`corner-badge corner-badge-tl corner-badge-xs corner-badge-pin corner-badge-persistent${cellIsSaved ? ' corner-badge-pin-unpinnable' : ''}`}
                          onClick={cellIsSaved ? (e) => {
                            e.stopPropagation();
                            handleSavePalette(cellBaseHex, cell.type.id, cell.depth.id, cell.palette, cell.elements);
                          } : undefined}
                          role={cellIsSaved ? 'button' : undefined}
                          tabIndex={cellIsSaved ? -1 : undefined}
                          title={cellDefaultPreset
                            ? `Default: ${PALETTE_PRESETS.find(p => p.id === cellDefaultPreset)?.name || cellDefaultPreset}`
                            : 'Remove from saved'}
                        >
                          <PinIcon className="w-2 h-2" />
                        </span>
                      ) : (
                        <span
                          className="corner-badge corner-badge-tl corner-badge-xs corner-badge-pin corner-badge-hover"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSavePalette(cellBaseHex, cell.type.id, cell.depth.id, cell.palette, cell.elements);
                          }}
                          role="button"
                          tabIndex={-1}
                          title="Save palette"
                        >
                          <PinIcon className="w-2 h-2" />
                        </span>
                      )}

                      {cell.palette && (
                        <PaletteSwatchGrid paletteColors={cell.palette} elements={cell.elements} />
                      )}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div className="palette-achromatic-note">
            Select a base colour to see harmonies
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* ③ SAVED PALETTES                                           */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <div className="palette-section palette-saved-section">
        <div className="harmony-section-label">Saved Palettes</div>
        <div className="palette-saved-row">
          {/* Default palettes — Magic Forest & Dark Ink (always present) */}
          {PALETTE_PRESETS.filter(p => DEFAULT_SAVED_IDS.includes(p.id)).map(preset => {
            const isActive = currentId === preset.id;
            const accentRgb = hexToRgb(preset.colors.accent);
            const accentGlow = `rgba(${accentRgb.r},${accentRgb.g},${accentRgb.b},0.25)`;
            return (
              <button
                key={preset.id}
                onClick={() => handlePresetClick(preset.id)}
                className={`palette-saved-chip group${isActive ? ' palette-saved-chip-active' : ''}`}
                title={preset.description}
              >
                <span className="palette-saved-led" style={isActive ? {
                  background: preset.colors.accent,
                  boxShadow: `0 0 4px ${accentGlow}`,
                } : undefined} />

                <PaletteSwatchGrid paletteColors={preset.colors} elements={preset.elements} />
                <span className={`palette-saved-name${isActive ? ' palette-saved-name-active' : ''}`}
                  style={isActive ? { color: preset.colors.accent } : undefined}
                >
                  {preset.name}
                </span>
              </button>
            );
          })}

          {/* User-saved palettes */}
          {savedPalettes.map(saved => {
            const isActive = currentId === 'custom'
              && customPalette?.accent === saved.palette.accent
              && customPalette?.accentSoft === saved.palette.accentSoft;
            const accentRgb = hexToRgb(saved.palette.accent);
            const accentGlow = accentRgb
              ? `rgba(${accentRgb.r},${accentRgb.g},${accentRgb.b},0.25)`
              : null;
            return (
              <button
                key={saved.id}
                onClick={() => handleApplySavedPalette(saved)}
                className={`palette-saved-chip group${isActive ? ' palette-saved-chip-active' : ''}`}
                title={saved.name}
              >
                {/* LED */}
                <span className="palette-saved-led" style={isActive ? {
                  background: saved.palette.accent,
                  boxShadow: `0 0 4px ${accentGlow}`,
                } : undefined} />

                {/* Delete badge — top-left corner */}
                <span
                  className="corner-badge corner-badge-tl corner-badge-xs corner-badge-delete corner-badge-hover"
                  onClick={(e) => { e.stopPropagation(); handleRemoveSavedPalette(saved.id); }}
                  role="button"
                  tabIndex={-1}
                  title={`Remove ${saved.name}`}
                >
                  <CrossIcon className="w-1.5 h-1.5" />
                </span>

                <PaletteSwatchGrid paletteColors={saved.palette} elements={saved.elements} />

                <span className={`palette-saved-name${isActive ? ' palette-saved-name-active' : ''}`}
                  style={isActive ? { color: saved.palette.accent } : undefined}
                >
                  {saved.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
